import React, { useEffect, useMemo, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, AreaChart, Area,
  ComposedChart, ScatterChart, Scatter, ReferenceLine, Cell, ZAxis,
} from 'recharts';
import {
  fetchSummary, SummaryData, SummaryOrder, SummaryExpense, SummaryStock, MarketEvent, ListingEvent, FloorStat,
} from '../services/summaryService';

/**
 * Summary screen: every graph is computed here from one payload (see summaryService). The date
 * filter applies to sales graphs; snapshot graphs (pipeline, deliveries due, floors, stock, market
 * vs my listings) always show the current state and say so in their notes.
 */

// ── labels, colours, helpers ───────────────────────────────────────────────────
const PLATFORM_LABEL: Record<string, string> = { LiveFootballTickets: 'Live', HelloTickets: 'Hello', Tixstock: 'TX', StubHub: 'StubHub', 'Vivid 1': 'Vivid 1', 'Vivid 2': 'Vivid 2', FTN: 'FTN', Private: 'Private' };
const PLATFORM_COLOR: Record<string, string> = { Live: '#2563eb', Hello: '#f59e0b', StubHub: '#7c3aed', TX: '#10b981', 'Vivid 1': '#ef4444', 'Vivid 2': '#f97316', FTN: '#0ea5e9', Private: '#64748b', Other: '#94a3b8' };
const PALETTE = ['#2563eb', '#f59e0b', '#10b981', '#7c3aed', '#ef4444', '#0ea5e9', '#f97316', '#64748b', '#ec4899', '#84cc16', '#14b8a6', '#a855f7'];
const DAY = 86400000;
const plat = (p: string) => PLATFORM_LABEL[p] || p || 'Other';
const color = (p: string, i = 0) => PLATFORM_COLOR[p] || PALETTE[i % PALETTE.length];
const gbp = (n: number) => '£' + Math.round(n).toLocaleString();
const gbp0 = (n: number) => (Math.abs(n) >= 1000 ? '£' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k' : '£' + Math.round(n));
const pct = (n: number) => (Number.isFinite(n) ? n.toFixed(0) + '%' : '-');
const monthKey = (d: Date) => d.toISOString().slice(0, 7);
const dayKey = (d: Date) => d.toISOString().slice(0, 10);
const weekKey = (d: Date) => { const x = new Date(d); const dow = (x.getUTCDay() + 6) % 7; x.setUTCDate(x.getUTCDate() - dow); return dayKey(x); };
const monthLabel = (k: string) => { const [y, m] = k.split('-'); return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1] + ' ' + y.slice(2); };
const median = (a: number[]) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[Math.floor(s.length / 2)] : 0; };
const quantile = (a: number[], q: number) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[Math.min(s.length - 1, Math.floor(s.length * q))] : 0; };
const sum = (a: number[]) => a.reduce((s, x) => s + x, 0);
const cat = (o: SummaryOrder) => String(o.category || '').trim().toUpperCase().replace(/\s+/g, ' ') || 'UNKNOWN';
const normEvent = (s: string) => String(s || '').toLowerCase().replace(/\b(fc|afc|cf|tickets?)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
const homeTeam = (s: string) => { const m = String(s || '').split(/\s+(?:vs\.?|v)\s+/i); return m.length > 1 ? m[0].replace(/^(champions league|uefa europa league|europa league|carabao cup|fa cup)\s*-\s*/i, '').trim() : ''; };
const competition = (s: string) => { if (/champions league/i.test(s)) return 'Champions League'; if (/europa/i.test(s)) return 'Europa League'; if (/carabao|efl cup|fa cup/i.test(s)) return 'Cups'; if (/\bv(s\.?)?\b/i.test(s)) return 'League'; return 'Concerts & other'; };
type Basis = 'Sale date' | 'Event date';
// Which date a sale is filed under: the toggle at the top decides for the month-based charts.
const keyDate = (o: SummaryOrder, basis: Basis) => new Date((basis === 'Event date' ? o.eventDate : o.orderDate) || o.orderDate || o.eventDate!);
const leadDays = (o: SummaryOrder) => (o.orderDate && o.eventDate ? (new Date(o.eventDate).getTime() - new Date(o.orderDate).getTime()) / DAY : null);
const leadBucket = (d: number) => (d < 3 ? '<3d' : d < 7 ? '3-7d' : d < 14 ? '7-14d' : d < 30 ? '14-30d' : d < 60 ? '30-60d' : '60d+');
const LEAD_ORDER = ['<3d', '3-7d', '7-14d', '14-30d', '30-60d', '60d+'];
const qtyBucket = (q: number) => (q >= 5 ? '5+' : String(q));

// ── small UI pieces ───────────────────────────────────────────────────────────
const Card: React.FC<{ title: string; note?: string; right?: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({ title, note, right, children, wide }) => (
  <div className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-900 p-3 ${wide ? 'xl:col-span-2' : ''}`}>
    <div className="flex items-start justify-between gap-2 mb-1">
      <div>
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</div>
        {note && <div className="text-xs text-slate-500 dark:text-slate-400">{note}</div>}
      </div>
      {right}
    </div>
    {children}
  </div>
);
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-base font-bold text-slate-700 dark:text-slate-200 mb-2 px-1">{title}</h2>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{children}</div>
  </div>
);
const Empty: React.FC<{ h?: number }> = ({ h = 260 }) => <div style={{ height: h }} className="flex items-center justify-center text-sm text-slate-400">No data for this filter</div>;
const Toggle: React.FC<{ value: string; options: string[]; onChange: (v: string) => void }> = ({ value, options, onChange }) => (
  <div className="flex gap-1">
    {options.map((o) => (
      <button key={o} onClick={() => onChange(o)} className={`px-2 py-0.5 rounded-full text-xs border ${value === o ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'}`}>{o}</button>
    ))}
  </div>
);
const tooltipStyle = { contentStyle: { fontSize: 12, borderRadius: 8 } };
// Tooltip for stacked charts: every series plus a Total row, which recharts' default omits.
const StackTooltip = ({ active, payload, label, fmt = (n: number) => String(n), suffix = '' }: any) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p: any) => p.value != null && p.value !== 0);
  const total = sum(rows.map((p: any) => Number(p.value) || 0));
  return (
    <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-xs shadow">
      <div className="font-semibold mb-1">{label}</div>
      {rows.map((p: any) => <div key={p.name} className="flex justify-between gap-4"><span style={{ color: p.color }}>{p.name}</span><span>{fmt(Number(p.value))}{suffix}</span></div>)}
      {rows.length > 1 && <div className="flex justify-between gap-4 border-t border-slate-200 dark:border-slate-600 mt-1 pt-1 font-semibold"><span>Total</span><span>{fmt(total)}{suffix}</span></div>}
    </div>
  );
};
const Grid = () => <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />;
const legendStyle = { wrapperStyle: { fontSize: 11 } };
const tick = { fontSize: 11 };

// ── the screen ────────────────────────────────────────────────────────────────
type Preset = '30d' | '90d' | '6m' | '12m' | 'YTD' | 'All' | 'Custom';

const SummaryContent: React.FC = () => {
  const [data, setData] = useState<SummaryData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState<Preset>('12m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [basis, setBasis] = useState<Basis>('Sale date');
  const [platforms, setPlatforms] = useState<Set<string> | null>(null); // null = all

  const load = async (refresh = false) => {
    setLoading(true); setError('');
    try { setData(await fetchSummary(refresh)); } catch (e: any) { setError(e?.response?.data?.message || e?.message || 'Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const range = useMemo(() => {
    // Presets look back from today. Under "Event date" the window stays open-ended into the
    // future, otherwise every game not yet played would vanish from the charts.
    const now = new Date(); const end = basis === 'Event date' ? new Date(2100, 0, 1) : new Date(now.getTime() + DAY);
    const start = new Date(now);
    if (preset === '30d') start.setDate(start.getDate() - 30);
    else if (preset === '90d') start.setDate(start.getDate() - 90);
    else if (preset === '6m') start.setMonth(start.getMonth() - 6);
    else if (preset === '12m') start.setMonth(start.getMonth() - 12);
    else if (preset === 'YTD') { start.setMonth(0, 1); start.setHours(0, 0, 0, 0); }
    else if (preset === 'All') return { start: new Date(2000, 0, 1), end: new Date(2100, 0, 1) };
    else return { start: from ? new Date(from) : new Date(2000, 0, 1), end: to ? new Date(new Date(to).getTime() + DAY) : new Date(2100, 0, 1) };
    return { start, end };
  }, [preset, from, to, basis]);

  const allPlatforms = useMemo(() => (data ? [...new Set(data.orders.map((o) => plat(o.platform)))].sort() : []), [data]);
  const rows = useMemo(() => {
    if (!data) return [] as SummaryOrder[];
    return data.orders.filter((o) => {
      const d = basis === 'Sale date' ? o.orderDate : o.eventDate;
      if (!d) return false;
      const t = new Date(d).getTime();
      if (t < range.start.getTime() || t >= range.end.getTime()) return false;
      return !platforms || platforms.has(plat(o.platform));
    });
  }, [data, range, basis, platforms]);
  const platformRows = useMemo(() => (data ? data.orders.filter((o) => !platforms || platforms.has(plat(o.platform))) : []), [data, platforms]);

  if (loading && !data) return <div className="p-10 flex justify-center"><IonSpinner /></div>;
  if (error && !data) return <div className="p-4 text-red-600 text-sm">{error}</div>;
  if (!data) return null;

  const totals = { orders: rows.length, tickets: sum(rows.map((o) => o.qty)), revenue: sum(rows.map((o) => o.revenue)) };
  const withCost = rows.filter((o) => o.cost != null);
  const margin = withCost.length ? (100 * (sum(withCost.map((o) => o.revenue)) - sum(withCost.map((o) => o.cost || 0)))) / sum(withCost.map((o) => o.revenue)) : NaN;

  return (
    <div className="p-3 max-w-[1500px] mx-auto">
      {/* filters */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-900 p-3 mb-4 flex flex-wrap items-center gap-3">
        <Toggle value={preset} options={['30d', '90d', '6m', '12m', 'YTD', 'All', 'Custom']} onChange={(v) => setPreset(v as Preset)} />
        {preset === 'Custom' && (
          <div className="flex items-center gap-1 text-xs">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-950" />
            <span>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-950" />
          </div>
        )}
        <Toggle value={basis} options={['Sale date', 'Event date']} onChange={(v) => setBasis(v as any)} />
        <div className="flex flex-wrap gap-1">
          {allPlatforms.map((p) => {
            const on = !platforms || platforms.has(p);
            return (
              <button key={p} onClick={() => { const s = new Set(platforms || allPlatforms); if (on) s.delete(p); else s.add(p); setPlatforms(s.size === allPlatforms.length ? null : s); }}
                className="px-2 py-0.5 rounded-full text-xs border" style={{ borderColor: color(p), background: on ? color(p) : 'transparent', color: on ? '#fff' : color(p) }}>{p}</button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span>{basis === 'Event date' && preset !== 'Custom' && preset !== 'All' ? `events from ${range.start.toLocaleDateString()} onward · ` : ''}{totals.orders.toLocaleString()} orders · {totals.tickets.toLocaleString()} tickets · {gbp(totals.revenue)} · margin {pct(margin)} on {withCost.length} costed</span>
          <button onClick={() => load(true)} className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600">{loading ? '…' : 'Refresh'}</button>
        </div>
      </div>

      <Section title="Sales and revenue">
        <RevenueByMonth rows={rows} basis={basis} />
        <OrdersPerWeek rows={rows} />
        <PlatformShare rows={rows} basis={basis} />
        <AvgPriceByMonth rows={rows} basis={basis} />
        <Heatmap rows={rows} />
        <TopEvents rows={rows} />
        <ByClub rows={rows} />
        <ByCompetition rows={rows} />
      </Section>

      <Section title="Margin and cost">
        <ProfitByMonth rows={rows} basis={basis} />
        <MarginBy rows={rows} />
        <ProfitBySupplier rows={rows} />
        <Uncosted orders={platformRows} />
        <NetProfit rows={rows} expenses={data.expenses} range={range} basis={basis} />
        <ExpensesByCategory expenses={data.expenses} range={range} />
      </Section>

      <Section title="Timing and lead time">
        <LeadTimeHistogram rows={rows} />
        <LeadTimeByMonth rows={rows} />
        <PriceVsLead rows={rows} />
        <SalesCurves orders={platformRows} />
        <Pipeline orders={platformRows} />
      </Section>

      <Section title="Delivery and operations">
        <DeliveryFunnel orders={platformRows} />
        <SaleToDelivery rows={rows} />
        <ResaleRate rows={rows} />
      </Section>

      <Section title="Market and pricing">
        <MyVsMarket listings={data.listings.events} />
        <Floors floors={data.listings.floors} />
        <MarketShare market={data.market.events} orders={data.orders} />
        <PriceDistribution rows={rows} />
      </Section>

      <Section title="Stock and inventory">
        <StockOnHand stock={data.stock} />
        <SeasonUsage stock={data.stock} />
      </Section>

      <Section title="Buyers">
        <Buyers rows={rows} all={data.orders} />
        <QuantityMix rows={rows} basis={basis} />
      </Section>
      <div className="text-[11px] text-slate-400 px-1 pb-6">Data generated {new Date(data.generatedAt).toLocaleString()} · cached for 10 minutes on the server · Refresh forces a recompute.</div>
    </div>
  );
};

export default SummaryContent;

// ── per-month / per-platform pivots ───────────────────────────────────────────
const pivot = (rows: SummaryOrder[], key: (o: SummaryOrder) => string, value: (o: SummaryOrder) => number) => {
  const m = new Map<string, Record<string, number>>(); const plats = new Set<string>();
  for (const o of rows) { const k = key(o); const p = plat(o.platform); plats.add(p); const e = m.get(k) || {}; e[p] = (e[p] || 0) + value(o); m.set(k, e); }
  const keys = [...m.keys()].sort();
  return { data: keys.map((k) => ({ k, label: /^\d{4}-\d{2}$/.test(k) ? monthLabel(k) : k, ...m.get(k)! })), plats: [...plats].sort() };
};
const StackedByPlatform: React.FC<{ data: any[]; plats: string[]; fmt?: (n: number) => string; percent?: boolean; area?: boolean }> = ({ data, plats, fmt = gbp0, percent, area }) => {
  const tipFmt = fmt === gbp0 ? gbp : fmt;
  if (!data.length) return <Empty />;
  const rows = percent ? data.map((d) => { const t = sum(plats.map((p) => d[p] || 0)) || 1; const r: any = { label: d.label }; plats.forEach((p) => { r[p] = Math.round((100 * (d[p] || 0)) / t); }); return r; }) : data;
  const Chart: any = area ? AreaChart : BarChart;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <Chart data={rows}>
        <Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={percent ? (v: number) => v + '%' : fmt} />
        <Tooltip content={<StackTooltip fmt={percent ? (n: number) => String(n) : tipFmt} suffix={percent ? '%' : ''} />} /><Legend {...legendStyle} />
        {plats.map((p, i) => (area
          ? <Area key={p} type="monotone" dataKey={p} stackId="a" stroke={color(p, i)} fill={color(p, i)} fillOpacity={0.8} />
          : <Bar key={p} dataKey={p} stackId="a" fill={color(p, i)} />))}
      </Chart>
    </ResponsiveContainer>
  );
};

const RevenueByMonth: React.FC<{ rows: SummaryOrder[]; basis: Basis }> = ({ rows, basis }) => {
  const [what, setWhat] = useState('Revenue');
  const p = useMemo(() => pivot(rows, (o) => monthKey(keyDate(o, basis)), (o) => (what === 'Revenue' ? o.revenue : what === 'Tickets' ? o.qty : 1)), [rows, what, basis]);
  return <Card title="1. Revenue by month, by platform" note={`All sales, whether or not the tickets have been bought yet, by ${basis === 'Event date' ? 'event' : 'sale'} month (toggle above). Stacked by platform; hover for the total.`} right={<Toggle value={what} options={['Revenue', 'Tickets', 'Orders']} onChange={setWhat} />}><StackedByPlatform data={p.data} plats={p.plats} fmt={what === 'Revenue' ? gbp0 : (n) => String(n)} /></Card>;
};

const OrdersPerWeek: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, { orders: number; tickets: number }>();
    for (const o of rows) { if (!o.orderDate) continue; const k = weekKey(new Date(o.orderDate)); const e = m.get(k) || { orders: 0, tickets: 0 }; e.orders++; e.tickets += o.qty; m.set(k, e); }
    const keys = [...m.keys()].sort();
    return keys.map((k, i) => { const win = keys.slice(Math.max(0, i - 3), i + 1).map((x) => m.get(x)!.tickets); return { label: k.slice(5), ...m.get(k)!, ma: Math.round(sum(win) / win.length) }; });
  }, [rows]);
  return (
    <Card title="2. Orders and tickets per week" note="Bars: orders and tickets by sale week. Line: 4-week moving average of tickets.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            <Bar dataKey="orders" fill="#94a3b8" /><Bar dataKey="tickets" fill="#2563eb" /><Line type="monotone" dataKey="ma" name="4-wk avg tickets" stroke="#ef4444" dot={false} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

const PlatformShare: React.FC<{ rows: SummaryOrder[]; basis: Basis }> = ({ rows, basis }) => {
  const p = useMemo(() => pivot(rows, (o) => monthKey(keyDate(o, basis)), (o) => o.revenue), [rows, basis]);
  return <Card title="3. Revenue share by platform" note={`Each ${basis === 'Event date' ? 'event' : 'sale'} month adds to 100%. Shows where the business is shifting.`}><StackedByPlatform data={p.data} plats={p.plats} percent area /></Card>;
};

const AvgPriceByMonth: React.FC<{ rows: SummaryOrder[]; basis: Basis }> = ({ rows, basis }) => {
  const { data, plats } = useMemo(() => {
    const m = new Map<string, Record<string, { r: number; t: number }>>(); const ps = new Set<string>();
    for (const o of rows) { if (!o.orderDate) continue; const k = monthKey(keyDate(o, basis)); const p = plat(o.platform); ps.add(p); const e = m.get(k) || {}; e[p] = e[p] || { r: 0, t: 0 }; e[p].r += o.revenue; e[p].t += o.qty; m.set(k, e); }
    const plats = [...ps].sort();
    return { plats, data: [...m.keys()].sort().map((k) => { const r: any = { label: monthLabel(k) }; for (const p of plats) if (m.get(k)![p]) r[p] = Math.round(m.get(k)![p].r / m.get(k)![p].t); return r; }) };
  }, [rows, basis]);
  return (
    <Card title="4. Average price per ticket by month" note={`Revenue ÷ tickets by ${basis === 'Event date' ? 'event' : 'sale'} month, one line per platform.`}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={gbp0} /><Tooltip {...tooltipStyle} formatter={(v: any) => gbp(Number(v))} /><Legend {...legendStyle} />
            {plats.map((p, i) => <Line key={p} type="monotone" dataKey={p} stroke={color(p, i)} dot={false} strokeWidth={2} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

const Heatmap: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const [what, setWhat] = useState('Revenue');
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const o of rows) { if (!o.orderDate) continue; const d = new Date(o.orderDate); g[(d.getDay() + 6) % 7][d.getHours()] += what === 'Revenue' ? o.revenue : 1; }
    return g;
  }, [rows, what]);
  const max = Math.max(1, ...grid.flat());
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <Card title="5. When people buy" note="Day of week × hour of day (your local time)." right={<Toggle value={what} options={['Revenue', 'Orders']} onChange={setWhat} />}>
      <div className="overflow-x-auto">
        <div className="grid gap-[2px]" style={{ gridTemplateColumns: '36px repeat(24, minmax(14px, 1fr))', minWidth: 420 }}>
          <div />{Array.from({ length: 24 }, (_, h) => <div key={h} className="text-[9px] text-center text-slate-400">{h}</div>)}
          {grid.map((row, d) => (
            <React.Fragment key={d}>
              <div className="text-[10px] text-slate-500 leading-4">{days[d]}</div>
              {row.map((v, h) => <div key={h} title={`${days[d]} ${h}:00 — ${what === 'Revenue' ? gbp(v) : v}`} className="h-4 rounded-sm" style={{ background: `rgba(37, 99, 235, ${v ? 0.12 + 0.88 * (v / max) : 0.04})` }} />)}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Card>
  );
};

const HBars: React.FC<{ data: { name: string; value: number; extra?: string }[]; fmt?: (n: number) => string; h?: number; colorBy?: (d: any, i: number) => string }> = ({ data, fmt = gbp0, h, colorBy }) => {
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={h || Math.max(200, 22 * data.length + 30)}>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
        <Grid /><XAxis type="number" tick={tick} tickFormatter={fmt} /><YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 10 }} interval={0} />
        <Tooltip {...tooltipStyle} formatter={(v: any, _n: any, p: any) => [fmt(Number(v)) + (p?.payload?.extra ? ` (${p.payload.extra})` : ''), '']} />
        <Bar dataKey="value" fill="#2563eb">{data.map((d, i) => <Cell key={i} fill={colorBy ? colorBy(d, i) : '#2563eb'} />)}</Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const TopEvents: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const [what, setWhat] = useState('Revenue');
  const data = useMemo(() => {
    const m = new Map<string, { r: number; t: number; n: number }>();
    for (const o of rows) { const k = o.eventName || '?'; const e = m.get(k) || { r: 0, t: 0, n: 0 }; e.r += o.revenue; e.t += o.qty; e.n++; m.set(k, e); }
    return [...m.entries()].map(([name, e]) => ({ name: name.slice(0, 40), value: what === 'Revenue' ? e.r : e.t, extra: `${e.n} orders, ${e.t} tickets` })).sort((a, b) => b.value - a.value).slice(0, 20);
  }, [rows, what]);
  return <Card title="6. Top 20 events" note="By revenue or tickets sold." right={<Toggle value={what} options={['Revenue', 'Tickets']} onChange={setWhat} />}><HBars data={data} fmt={what === 'Revenue' ? gbp0 : (n) => String(n)} /></Card>;
};

const ByClub: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, { r: number; t: number; n: number }>();
    for (const o of rows) { const k = homeTeam(o.eventName) || (competition(o.eventName) === 'Concerts & other' ? o.eventName : '?'); const e = m.get(k) || { r: 0, t: 0, n: 0 }; e.r += o.revenue; e.t += o.qty; e.n++; m.set(k, e); }
    return [...m.entries()].map(([name, e]) => ({ name: name.slice(0, 30), value: e.r, extra: `${e.n} orders, ${e.t} tickets, ${gbp(e.r / e.t)}/ticket` })).sort((a, b) => b.value - a.value).slice(0, 18);
  }, [rows]);
  return <Card title="7a. Revenue by club (home team)" note="Concerts show by artist."><HBars data={data} /></Card>;
};

const ByCompetition: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, { r: number; t: number; n: number }>();
    for (const o of rows) { const k = competition(o.eventName); const e = m.get(k) || { r: 0, t: 0, n: 0 }; e.r += o.revenue; e.t += o.qty; e.n++; m.set(k, e); }
    return [...m.entries()].map(([name, e]) => ({ name, value: e.r, extra: `${e.n} orders, ${e.t} tickets, ${gbp(e.r / e.t)}/ticket` })).sort((a, b) => b.value - a.value);
  }, [rows]);
  return <Card title="7b. Revenue by competition" note="League, Champions League, Europa, cups, concerts."><HBars data={data} h={220} colorBy={(_d, i) => PALETTE[i]} /></Card>;
};

// ── margin ────────────────────────────────────────────────────────────────────
const ProfitByMonth: React.FC<{ rows: SummaryOrder[]; basis: Basis }> = ({ rows, basis }) => {
  const data = useMemo(() => {
    const m = new Map<string, { rev: number; cost: number; n: number; all: number }>();
    for (const o of rows) { if (!o.orderDate) continue; const k = monthKey(keyDate(o, basis)); const e = m.get(k) || { rev: 0, cost: 0, n: 0, all: 0 }; e.all++; if (o.cost != null) { e.rev += o.revenue; e.cost += o.cost; e.n++; } m.set(k, e); }
    return [...m.keys()].sort().map((k) => { const e = m.get(k)!; return { label: monthLabel(k), revenue: Math.round(e.rev), cost: Math.round(e.cost), profit: Math.round(e.rev - e.cost), margin: e.rev ? Math.round((100 * (e.rev - e.cost)) / e.rev) : null, coverage: Math.round((100 * e.n) / e.all) }; });
  }, [rows, basis]);
  return (
    <Card title="8. Profit by month" note={`By ${basis === 'Event date' ? 'event' : 'sale'} month. Only orders with a recorded cost. Bars: revenue vs cost. Lines: margin % and share of orders that are costed.`}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis yAxisId="l" tick={tick} tickFormatter={gbp0} /><YAxis yAxisId="r" orientation="right" tick={tick} tickFormatter={(v) => v + '%'} domain={[0, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v: any, n: any) => (n === 'margin' || n === 'coverage' ? v + '%' : gbp(Number(v)))} /><Legend {...legendStyle} />
            <Bar yAxisId="l" dataKey="revenue" fill="#2563eb" /><Bar yAxisId="l" dataKey="cost" fill="#f59e0b" /><Bar yAxisId="l" dataKey="profit" fill="#10b981" />
            <Line yAxisId="r" type="monotone" dataKey="margin" stroke="#ef4444" strokeWidth={2} dot={false} connectNulls /><Line yAxisId="r" type="monotone" dataKey="coverage" name="costed %" stroke="#94a3b8" strokeDasharray="4 2" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

const marginTable = (rows: SummaryOrder[], key: (o: SummaryOrder) => string) => {
  const m = new Map<string, { rev: number; cost: number; n: number; t: number }>();
  for (const o of rows) { if (o.cost == null) continue; const k = key(o); const e = m.get(k) || { rev: 0, cost: 0, n: 0, t: 0 }; e.rev += o.revenue; e.cost += o.cost; e.n++; e.t += o.qty; m.set(k, e); }
  return [...m.entries()].map(([name, e]) => ({ name, value: e.rev ? Math.round((100 * (e.rev - e.cost)) / e.rev) : 0, profit: e.rev - e.cost, extra: `${e.n} orders, profit ${gbp(e.rev - e.cost)}, ${gbp((e.rev - e.cost) / e.t)}/ticket` }));
};
const MarginBy: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const [what, setWhat] = useState('Platform');
  const data = useMemo(() => marginTable(rows, (o) => (what === 'Platform' ? plat(o.platform) : cat(o).slice(0, 28))).filter((d) => /\d+ orders/.test(d.extra)).sort((a, b) => b.profit - a.profit).slice(0, 18), [rows, what]);
  return <Card title="9. Margin by platform and by category" note="Margin % on costed orders; sorted by total profit." right={<Toggle value={what} options={['Platform', 'Category']} onChange={setWhat} />}><HBars data={data} fmt={(n) => n + '%'} colorBy={(d) => (d.value < 0 ? '#ef4444' : d.value < 15 ? '#f59e0b' : '#10b981')} /></Card>;
};
const ProfitBySupplier: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => marginTable(rows.filter((o) => o.boughtFrom), (o) => o.boughtFrom.slice(0, 26)).map((d) => ({ ...d, value: d.profit, extra: d.extra })).sort((a, b) => b.value - a.value).slice(0, 18), [rows]);
  return <Card title="10. Profit by supplier" note="Who you buy from and what their tickets made you."><HBars data={data} colorBy={(d) => (d.value < 0 ? '#ef4444' : '#2563eb')} /></Card>;
};
const Uncosted: React.FC<{ orders: SummaryOrder[] }> = ({ orders }) => {
  const data = useMemo(() => {
    const now = Date.now();
    const m = new Map<string, { n: number; r: number; fut: number }>();
    for (const o of orders) { if (o.cost != null || o.boughtFrom || !o.eventDate) continue; const k = monthKey(new Date(o.eventDate)); const e = m.get(k) || { n: 0, r: 0, fut: 0 }; e.n++; e.r += o.revenue; if (new Date(o.eventDate).getTime() > now) e.fut += o.revenue; m.set(k, e); }
    return [...m.keys()].sort().slice(-14).map((k) => ({ label: monthLabel(k), orders: m.get(k)!.n, revenue: Math.round(m.get(k)!.r) }));
  }, [orders]);
  const fut = orders.filter((o) => o.cost == null && !o.boughtFrom && o.eventDate && new Date(o.eventDate).getTime() > Date.now());
  return (
    <Card title="11. Orders with no supplier or cost yet" note={`By event month. Future events: ${fut.length} orders, ${sum(fut.map((o) => o.qty))} tickets, ${gbp(sum(fut.map((o) => o.revenue)))} still to source or record.`}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis yAxisId="l" tick={tick} tickFormatter={gbp0} /><YAxis yAxisId="r" orientation="right" tick={tick} /><Tooltip {...tooltipStyle} formatter={(v: any, n: any) => (n === 'revenue' ? gbp(Number(v)) : v)} /><Legend {...legendStyle} />
            <Bar yAxisId="l" dataKey="revenue" fill="#f59e0b" /><Line yAxisId="r" dataKey="orders" stroke="#2563eb" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const NetProfit: React.FC<{ rows: SummaryOrder[]; expenses: SummaryExpense[]; range: { start: Date; end: Date }; basis: Basis }> = ({ rows, expenses, range, basis }) => {
  const data = useMemo(() => {
    const m = new Map<string, { gp: number; ex: number }>();
    for (const o of rows) { if (!o.orderDate || o.cost == null) continue; const k = monthKey(keyDate(o, basis)); const e = m.get(k) || { gp: 0, ex: 0 }; e.gp += o.revenue - o.cost; m.set(k, e); }
    for (const x of expenses) { const d = new Date(x.date); if (d < range.start || d >= range.end) continue; const k = monthKey(d); const e = m.get(k) || { gp: 0, ex: 0 }; e.ex += x.amountGBP; m.set(k, e); }
    return [...m.keys()].sort().map((k) => ({ label: monthLabel(k), 'gross profit': Math.round(m.get(k)!.gp), expenses: Math.round(m.get(k)!.ex), net: Math.round(m.get(k)!.gp - m.get(k)!.ex) }));
  }, [rows, expenses, range, basis]);
  return (
    <Card title="12a. Gross profit vs expenses, net per month" note="Gross profit = revenue − ticket cost on costed orders. Expenses from the Expenses screen.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={gbp0} /><Tooltip {...tooltipStyle} formatter={(v: any) => gbp(Number(v))} /><Legend {...legendStyle} />
            <Bar dataKey="gross profit" fill="#10b981" /><Bar dataKey="expenses" fill="#ef4444" /><Line type="monotone" dataKey="net" stroke="#2563eb" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const ExpensesByCategory: React.FC<{ expenses: SummaryExpense[]; range: { start: Date; end: Date } }> = ({ expenses, range }) => {
  const { data, cats } = useMemo(() => {
    const m = new Map<string, Record<string, number>>(); const cs = new Map<string, number>();
    for (const x of expenses) { const d = new Date(x.date); if (d < range.start || d >= range.end) continue; const k = monthKey(d); const c = x.category || 'Other'; cs.set(c, (cs.get(c) || 0) + x.amountGBP); const e = m.get(k) || {}; e[c] = (e[c] || 0) + x.amountGBP; m.set(k, e); }
    const cats = [...cs.entries()].sort((a, b) => b[1] - a[1]).map((x) => x[0]).slice(0, 10);
    return { cats, data: [...m.keys()].sort().map((k) => ({ label: monthLabel(k), ...Object.fromEntries(cats.map((c) => [c, Math.round(m.get(k)![c] || 0)])) })) };
  }, [expenses, range]);
  return (
    <Card title="12b. Expenses by month and category" note="Top 10 categories, stacked.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={gbp0} /><Tooltip content={<StackTooltip fmt={gbp} />} /><Legend {...legendStyle} />
            {cats.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={PALETTE[i % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

// ── timing ────────────────────────────────────────────────────────────────────
const LeadTimeHistogram: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const { data, plats } = useMemo(() => {
    const p = pivot(rows.filter((o) => leadDays(o) != null && leadDays(o)! >= 0), (o) => leadBucket(leadDays(o)!), () => 1);
    return { plats: p.plats, data: LEAD_ORDER.map((b) => p.data.find((d) => d.k === b) || { k: b, label: b }) };
  }, [rows]);
  return <Card title="13a. Lead time: days between sale and event" note="How far ahead buyers book, by platform."><StackedByPlatform data={data} plats={plats} fmt={(n) => String(n)} /></Card>;
};
const LeadTimeByMonth: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const o of rows) { const l = leadDays(o); if (l == null || l < 0 || !o.orderDate) continue; const k = monthKey(new Date(o.orderDate)); (m.get(k) || m.set(k, []).get(k)!).push(l); }
    return [...m.keys()].sort().map((k) => ({ label: monthLabel(k), median: Math.round(median(m.get(k)!)), p25: Math.round(quantile(m.get(k)!, 0.25)), p75: Math.round(quantile(m.get(k)!, 0.75)) }));
  }, [rows]);
  return (
    <Card title="13b. Lead time per month" note="Median days before the event, with the 25th–75th percentile band.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            <Area type="monotone" dataKey="p75" stroke="none" fill="#2563eb" fillOpacity={0.15} name="75th pct" /><Area type="monotone" dataKey="p25" stroke="none" fill="#ffffff" fillOpacity={0.9} name="25th pct" /><Line type="monotone" dataKey="median" stroke="#2563eb" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const PriceVsLead: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const { data, cats } = useMemo(() => {
    const counts = new Map<string, number>(); rows.forEach((o) => counts.set(cat(o), (counts.get(cat(o)) || 0) + 1));
    const cats = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map((x) => x[0]);
    const m = new Map<string, Record<string, number[]>>();
    for (const o of rows) { const l = leadDays(o); if (l == null || l < 0) continue; const c = cat(o); if (!cats.includes(c)) continue; const b = leadBucket(l); const e = m.get(b) || {}; (e[c] = e[c] || []).push(o.revenue / o.qty); m.set(b, e); }
    return { cats, data: LEAD_ORDER.map((b) => ({ label: b, ...Object.fromEntries(cats.map((c) => [c, m.get(b)?.[c]?.length ? Math.round(median(m.get(b)![c])) : null])) })) };
  }, [rows]);
  return (
    <Card title="14. Price per ticket vs lead time" note="Median £ per ticket by booking window, for the 6 most-sold categories.">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={gbp0} /><Tooltip {...tooltipStyle} formatter={(v: any) => gbp(Number(v))} /><Legend {...legendStyle} />
          {cats.map((c, i) => <Line key={c} type="monotone" dataKey={c} stroke={PALETTE[i]} strokeWidth={2} connectNulls />)}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};
const SalesCurves: React.FC<{ orders: SummaryOrder[] }> = ({ orders }) => {
  const { data, names } = useMemo(() => {
    const now = Date.now();
    const byEvent = new Map<string, SummaryOrder[]>();
    for (const o of orders) { if (!o.eventDate || !o.orderDate) continue; const t = new Date(o.eventDate).getTime(); if (t < now || t > now + 120 * DAY) continue; const k = `${o.eventName} (${dayKey(new Date(o.eventDate)).slice(5)})`; (byEvent.get(k) || byEvent.set(k, []).get(k)!).push(o); }
    const names = [...byEvent.entries()].sort((a, b) => sum(b[1].map((o) => o.qty)) - sum(a[1].map((o) => o.qty))).slice(0, 10).map((x) => x[0]);
    const data = Array.from({ length: 91 }, (_, i) => { const days = 90 - i; const r: any = { days }; for (const n of names) r[n] = sum(byEvent.get(n)!.filter((o) => leadDays(o)! >= days).map((o) => o.qty)); return r; });
    return { data, names };
  }, [orders]);
  return (
    <Card title="15. Sales curve for upcoming events" note="Cumulative tickets sold against days before kick-off, for the 10 best-selling events in the next 120 days. Always current." wide>
      {names.length ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}><Grid /><XAxis dataKey="days" tick={tick} reversed label={{ value: 'days before event', position: 'insideBottom', offset: -2, fontSize: 11 }} /><YAxis tick={tick} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            {names.map((n, i) => <Line key={n} type="stepAfter" dataKey={n} stroke={PALETTE[i]} dot={false} strokeWidth={2} />)}
          </LineChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const Pipeline: React.FC<{ orders: SummaryOrder[] }> = ({ orders }) => {
  const p = useMemo(() => pivot(orders.filter((o) => o.eventDate && new Date(o.eventDate).getTime() > Date.now()), (o) => weekKey(new Date(o.eventDate!)), (o) => o.revenue), [orders]);
  const fut = orders.filter((o) => o.eventDate && new Date(o.eventDate).getTime() > Date.now());
  return <Card title="16. Pipeline: revenue already sold for future events" note={`By event week. ${fut.length} orders, ${sum(fut.map((o) => o.qty))} tickets, ${gbp(sum(fut.map((o) => o.revenue)))} still to deliver. Always current.`}><StackedByPlatform data={p.data.map((d) => ({ ...d, label: d.k.slice(5) }))} plats={p.plats} /></Card>;
};

// ── delivery ──────────────────────────────────────────────────────────────────
const DeliveryFunnel: React.FC<{ orders: SummaryOrder[] }> = ({ orders }) => {
  const [horizon, setHorizon] = useState('14d');
  const data = useMemo(() => {
    const now = Date.now(); const h = horizon === '7d' ? 7 : horizon === '14d' ? 14 : 30;
    const up = orders.filter((o) => o.eventDate && new Date(o.eventDate).getTime() > now && new Date(o.eventDate).getTime() < now + h * DAY);
    const m = new Map<string, Record<string, number>>();
    for (const o of up) { const p = plat(o.platform); const e = m.get(p) || {}; const k = o.deliveredAt ? `delivered (${o.deliveryMethod})` : 'not delivered'; e[k] = (e[k] || 0) + 1; m.set(p, e); }
    return [...m.entries()].map(([label, e]) => ({ label, ...e }));
  }, [orders, horizon]);
  const keys = [...new Set(data.flatMap((d) => Object.keys(d).filter((k) => k !== 'label')))].sort();
  return (
    <Card title="17. Delivery status for upcoming orders" note="Orders for events in the next days, delivered (by method) vs not yet. Always current." right={<Toggle value={horizon} options={['7d', '14d', '30d']} onChange={setHorizon} />}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} /><Tooltip content={<StackTooltip />} /><Legend {...legendStyle} />
            {keys.map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={k === 'not delivered' ? '#ef4444' : PALETTE[(i + 2) % PALETTE.length]} />)}
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const SaleToDelivery: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const o of rows) { if (!o.deliveredAt || !o.orderDate) continue; const k = weekKey(new Date(o.deliveredAt)); (m.get(k) || m.set(k, []).get(k)!).push((new Date(o.deliveredAt).getTime() - new Date(o.orderDate).getTime()) / DAY); }
    return [...m.keys()].sort().map((k) => ({ label: k.slice(5), median: Math.round(median(m.get(k)!) * 10) / 10, n: m.get(k)!.length }));
  }, [rows]);
  return (
    <Card title="18. Days from sale to delivery" note="Median per delivery week, orders that have a delivery timestamp.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis yAxisId="l" tick={tick} /><YAxis yAxisId="r" orientation="right" tick={tick} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            <Bar yAxisId="r" dataKey="n" name="deliveries" fill="#cbd5e1" /><Line yAxisId="l" type="monotone" dataKey="median" name="median days" stroke="#2563eb" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const ResaleRate: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const data = useMemo(() => {
    const m = new Map<string, { n: number; r: number }>();
    for (const o of rows) { if (!o.orderDate) continue; const k = monthKey(new Date(o.orderDate)); const e = m.get(k) || { n: 0, r: 0 }; e.n++; if (o.resold) e.r++; m.set(k, e); }
    return [...m.keys()].sort().map((k) => ({ label: monthLabel(k), resold: m.get(k)!.r, rate: Math.round((1000 * m.get(k)!.r) / m.get(k)!.n) / 10 }));
  }, [rows]);
  return (
    <Card title="19. Resales per month" note="Orders marked resold (buyer replaced) and their share of that month's orders.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis yAxisId="l" tick={tick} /><YAxis yAxisId="r" orientation="right" tick={tick} tickFormatter={(v) => v + '%'} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            <Bar yAxisId="l" dataKey="resold" fill="#f59e0b" /><Line yAxisId="r" type="monotone" dataKey="rate" name="rate %" stroke="#ef4444" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

// ── market ────────────────────────────────────────────────────────────────────
const MyVsMarket: React.FC<{ listings: ListingEvent[] }> = ({ listings }) => {
  const platsAll = [...new Set(listings.map((l) => l.platform))].sort();
  const [p, setP] = useState(platsAll[0] || '');
  const points = useMemo(() => listings.filter((l) => l.platform === (p || platsAll[0])).flatMap((l) => l.categories.filter((c) => c.compMin > 0 && c.myMin > 0).map((c) => ({ x: c.compMin, y: c.myMin, name: `${l.eventName} · ${c.name}`, ratio: c.myMin / c.compMin }))), [listings, p, platsAll]);
  const max = Math.min(3000, Math.max(100, quantile(points.map((x) => Math.max(x.x, x.y)), 0.97)));
  const over = [...points].filter((x) => x.ratio > 1.15).sort((a, b) => b.ratio - a.ratio).slice(0, 8);
  const under = [...points].filter((x) => x.ratio < 0.85).sort((a, b) => a.ratio - b.ratio).slice(0, 8);
  const cheapest = points.filter((x) => x.y <= x.x).length;
  return (
    <Card title="20. My cheapest price vs the market's cheapest, per event and category" note={`Latest bot snapshot. Each dot is one category of one event; on the diagonal you match the cheapest rival, above it you are dearer. I am cheapest or equal in ${cheapest} of ${points.length} cells.`} right={<Toggle value={p || platsAll[0]} options={platsAll} onChange={setP} />} wide>
      {points.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <div className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ left: 10, right: 20, bottom: 10 }}><Grid /><XAxis type="number" dataKey="x" name="market cheapest" tick={tick} domain={[0, max]} allowDataOverflow label={{ value: 'market cheapest', position: 'insideBottom', offset: -5, fontSize: 11 }} /><YAxis type="number" dataKey="y" name="my cheapest" tick={tick} domain={[0, max]} allowDataOverflow /><ZAxis range={[14, 14]} />
                <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: '3 3' }} content={({ payload }) => payload?.length ? <div className="bg-white dark:bg-gray-800 border rounded p-2 text-xs">{payload[0].payload.name}<br />me {payload[0].payload.y} · market {payload[0].payload.x}</div> : null} />
                <ReferenceLine segment={[{ x: 0, y: 0 }, { x: max, y: max }]} stroke="#ef4444" strokeDasharray="4 4" />
                <Scatter data={points} fill="#2563eb" fillOpacity={0.5} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] space-y-2">
            <div><div className="font-semibold text-red-600">Dearest vs market</div>{over.map((x) => <div key={x.name} className="truncate" title={x.name}>{Math.round(100 * x.ratio - 100)}% · {x.name}</div>)}</div>
            <div><div className="font-semibold text-emerald-600">Cheapest vs market</div>{under.map((x) => <div key={x.name} className="truncate" title={x.name}>−{Math.round(100 - 100 * x.ratio)}% · {x.name}</div>)}</div>
          </div>
        </div>) : <Empty />}
    </Card>
  );
};
const Floors: React.FC<{ floors: FloorStat[] }> = ({ floors }) => {
  const data = floors.map((f) => { const t = f.withFloor || 1; return { label: `${f.platform} (${f.listings.toLocaleString()})`, 'below floor': Math.round((100 * f.below) / t), 'at floor': Math.round((100 * f.atFloor) / t), 'within 5%': Math.round((100 * f.within5) / t), 'within 15%': Math.round((100 * f.within15) / t), 'well above': Math.round((100 * f.above) / t), noFloor: f.sampled - f.withFloor, sampled: f.sampled }; });
  return (
    <Card title="21. Listings sitting on their floor, per platform" note="Share of listings at, near or above their minimum price (sample of up to 3,000 listings per platform from the latest bot snapshot). A platform mostly at floor has no room to go lower.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}><Grid /><XAxis type="number" tick={tick} domain={[0, 100]} tickFormatter={(v) => v + '%'} /><YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 10 }} /><Tooltip {...tooltipStyle} formatter={(v: any) => v + '%'} /><Legend {...legendStyle} />
            {['below floor', 'at floor', 'within 5%', 'within 15%', 'well above'].map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={['#ef4444', '#f59e0b', '#fbbf24', '#60a5fa', '#10b981'][i]} />)}
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const MarketShare: React.FC<{ market: MarketEvent[]; orders: SummaryOrder[] }> = ({ market, orders }) => {
  const [src, setSrc] = useState('live');
  const data = useMemo(() => {
    const today = dayKey(new Date());
    const mine = new Map<string, number>();
    for (const o of orders) { if (!o.eventDate) continue; const k = normEvent(o.eventName) + '|' + dayKey(new Date(o.eventDate)); mine.set(k, (mine.get(k) || 0) + o.qty); }
    const rows = new Map<string, { label: string; market: number; mine: number; median: number }>();
    for (const e of market) { if (e.source !== src || e.day < today) continue; const k = normEvent(e.eventName) + '|' + e.day; const r = rows.get(k) || { label: `${e.eventName.slice(0, 28)} ${e.day.slice(5)}`, market: 0, mine: 0, median: e.medianPrice }; r.market += e.tickets - (src === 'live' ? e.myTickets : 0); r.mine = mine.get(k) || (src === 'live' ? e.myTickets : 0); rows.set(k, r); }
    return [...rows.values()].sort((a, b) => b.market + b.mine - a.market - a.mine).slice(0, 20).map((r) => ({ ...r, share: Math.round((100 * r.mine) / Math.max(1, r.market + r.mine)) }));
  }, [market, orders, src]);
  return (
    <Card title="22. Market share per upcoming match" note="Tickets the market sold (gathered Live or StubHub sales) vs tickets I sold on all platforms for the same match. Top 20 by volume." right={<Toggle value={src} options={['live', 'stubhub']} onChange={setSrc} />} wide>
      {data.length ? (
        <ResponsiveContainer width="100%" height={Math.max(220, 22 * data.length + 30)}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}><Grid /><XAxis type="number" tick={tick} /><YAxis type="category" dataKey="label" width={210} tick={{ fontSize: 10 }} interval={0} /><Tooltip {...tooltipStyle} formatter={(v: any, n: any, p: any) => [v, n === 'mine' ? `mine (${p.payload.share}% share, market median ${gbp(p.payload.median)})` : n]} /><Legend {...legendStyle} />
            <Bar dataKey="market" stackId="a" fill="#cbd5e1" /><Bar dataKey="mine" stackId="a" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const PriceDistribution: React.FC<{ rows: SummaryOrder[] }> = ({ rows }) => {
  const [what, setWhat] = useState('Category');
  const data = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const o of rows) { const k = what === 'Category' ? cat(o).slice(0, 26) : plat(o.platform); (m.get(k) || m.set(k, []).get(k)!).push(o.revenue / o.qty); }
    return [...m.entries()].filter(([, a]) => a.length >= 5).map(([name, a]) => ({ name, n: a.length, iqr: [Math.round(quantile(a, 0.25)), Math.round(quantile(a, 0.75))], median: Math.round(median(a)), p10: Math.round(quantile(a, 0.1)), p90: Math.round(quantile(a, 0.9)) })).sort((a, b) => b.median - a.median).slice(0, 20);
  }, [rows, what]);
  return (
    <Card title="23. Price per ticket distribution" note="Bar = 25th to 75th percentile, dot = median. Categories with at least 5 sales." right={<Toggle value={what} options={['Category', 'Platform']} onChange={setWhat} />}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={Math.max(220, 22 * data.length + 30)}>
          <ComposedChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}><Grid /><XAxis type="number" tick={tick} tickFormatter={gbp0} /><YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 10 }} interval={0} />
            <Tooltip {...tooltipStyle} formatter={(v: any, n: any, p: any) => (n === 'iqr' ? [`${gbp(p.payload.iqr[0])} – ${gbp(p.payload.iqr[1])} (${p.payload.n} sales, p10 ${gbp(p.payload.p10)}, p90 ${gbp(p.payload.p90)})`, '25–75%'] : [gbp(Number(v)), n])} />
            <Bar dataKey="iqr" fill="#93c5fd" /><Scatter dataKey="median" fill="#1d4ed8" />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

// ── stock ─────────────────────────────────────────────────────────────────────
const StockOnHand: React.FC<{ stock: SummaryStock[] }> = ({ stock }) => {
  const data = useMemo(() => {
    const rows: { name: string; value: number; extra: string }[] = [];
    for (const s of stock) {
      if (s.kind === 'event') { const rem = s.remaining ?? Math.max(0, s.quantity - s.assignedTotal); rows.push({ name: `${s.eventName} · ${s.category}`.slice(0, 40), value: rem * s.pricePerTicket, extra: `${rem} of ${s.quantity} left` }); }
      else { const rem = sum((s.usage || []).map((u) => u.remaining)); rows.push({ name: `${s.team} season · ${s.category}`.slice(0, 40), value: rem * s.pricePerTicket, extra: `${rem} seats left over ${(s.usage || []).length} games` }); }
    }
    return rows.filter((r) => r.value > 0).sort((a, b) => b.value - a.value).slice(0, 20);
  }, [stock]);
  return <Card title="24. Stock on hand: cost tied up in unsold tickets" note="Remaining tickets × cost per ticket, from the Stock screen. Always current."><HBars data={data} /></Card>;
};
const SeasonUsage: React.FC<{ stock: SummaryStock[] }> = ({ stock }) => {
  const seasons = stock.filter((s) => s.kind === 'season' && s.usage?.length);
  const [i, setI] = useState(0);
  const s = seasons[Math.min(i, seasons.length - 1)];
  const data = useMemo(() => (s ? [...(s.usage || [])].sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))).map((u) => ({ label: u.eventName.replace(/^.*?\s(?:vs\.?|v)\s/i, '').slice(0, 18) + (u.date ? ` ${String(u.date).slice(5, 10)}` : ''), used: u.used, remaining: u.remaining })) : []), [s]);
  return (
    <Card title="25. Season ticket usage" note={s ? `${s.team} · ${s.category} · ${s.quantity} seats per game. Sold vs unsold per home game. Always current.` : 'No season tickets in stock.'} right={seasons.length > 1 ? <Toggle value={String(Math.min(i, seasons.length - 1))} options={seasons.map((_x, k) => String(k))} onChange={(v) => setI(Number(v))} /> : undefined}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data}><Grid /><XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={70} /><YAxis tick={tick} /><Tooltip {...tooltipStyle} /><Legend {...legendStyle} />
            <Bar dataKey="used" stackId="a" fill="#10b981" /><Bar dataKey="remaining" stackId="a" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};

// ── buyers ────────────────────────────────────────────────────────────────────
const Buyers: React.FC<{ rows: SummaryOrder[]; all: SummaryOrder[] }> = ({ rows, all }) => {
  const data = useMemo(() => {
    const first = new Map<string, number>();
    for (const o of all) { if (!o.buyer || !o.orderDate) continue; const t = new Date(o.orderDate).getTime(); if (!first.has(o.buyer) || t < first.get(o.buyer)!) first.set(o.buyer, t); }
    const m = new Map<string, { nb: number; rb: number; nrev: number; rrev: number }>();
    for (const o of rows) { if (!o.orderDate) continue; const k = monthKey(new Date(o.orderDate)); const e = m.get(k) || { nb: 0, rb: 0, nrev: 0, rrev: 0 }; const isNew = !o.buyer || Math.abs(first.get(o.buyer)! - new Date(o.orderDate).getTime()) < 60000; if (isNew) { e.nb++; e.nrev += o.revenue; } else { e.rb++; e.rrev += o.revenue; } m.set(k, e); }
    return [...m.keys()].sort().map((k) => ({ label: monthLabel(k), 'new buyers': m.get(k)!.nb, returning: m.get(k)!.rb, 'returning revenue': Math.round(m.get(k)!.rrev) }));
  }, [rows, all]);
  const buyers = new Set(all.map((o) => o.buyer).filter(Boolean)); const repeat = [...buyers].filter((b) => all.filter((o) => o.buyer === b).length > 1).length;
  return (
    <Card title="26. New vs returning buyers" note={`Buyers matched by phone (or email/name). ${repeat} of ${buyers.size} buyers have bought more than once.`}>
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis yAxisId="l" tick={tick} /><YAxis yAxisId="r" orientation="right" tick={tick} tickFormatter={gbp0} /><Tooltip {...tooltipStyle} formatter={(v: any, n: any) => (String(n).includes('revenue') ? gbp(Number(v)) : v)} /><Legend {...legendStyle} />
            <Bar yAxisId="l" dataKey="new buyers" stackId="a" fill="#94a3b8" /><Bar yAxisId="l" dataKey="returning" stackId="a" fill="#2563eb" /><Line yAxisId="r" type="monotone" dataKey="returning revenue" stroke="#10b981" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
const QuantityMix: React.FC<{ rows: SummaryOrder[]; basis: Basis }> = ({ rows, basis }) => {
  const data = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    for (const o of rows) { if (!o.orderDate) continue; const k = monthKey(keyDate(o, basis)); const e = m.get(k) || {}; const q = qtyBucket(o.qty); e[q] = (e[q] || 0) + 1; m.set(k, e); }
    return [...m.keys()].sort().map((k) => { const e = m.get(k)!; const t = sum(Object.values(e)) || 1; return { label: monthLabel(k), ...Object.fromEntries(['1', '2', '3', '4', '5+'].map((q) => [q, Math.round((100 * (e[q] || 0)) / t)])) }; });
  }, [rows, basis]);
  return (
    <Card title="27. Quantity mix per month" note="Share of orders that were singles, pairs, trios, quads or 5+.">
      {data.length ? (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}><Grid /><XAxis dataKey="label" tick={tick} /><YAxis tick={tick} tickFormatter={(v) => v + '%'} /><Tooltip {...tooltipStyle} formatter={(v: any) => v + '%'} /><Legend {...legendStyle} />
            {['1', '2', '3', '4', '5+'].map((q, i) => <Bar key={q} dataKey={q} name={q === '1' ? 'singles' : q === '2' ? 'pairs' : q === '3' ? 'trios' : q === '4' ? 'quads' : '5+'} stackId="a" fill={PALETTE[i]} />)}
          </BarChart>
        </ResponsiveContainer>) : <Empty />}
    </Card>
  );
};
